import React, { useEffect, useRef, useState } from 'react';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import Table from '../../../core/common/dataTable/index';
import {
  createOrganisation,
  fetchOrganisationStatuses,
  fetchOrganisations,
  updateOrganisation,
  upgradeSubscription,
} from '../../../core/data/redux/service/OrganisationService';
import { formatError, formatSuccess } from '../../../core/data/redux/service/AuthService';
import Swal from 'sweetalert2';
import { AxiosResponse } from 'axios';
import Loader from '../../Loader/Loader';

type StatusOption = { value: string; label: string };

type OrgRow = {
  orgCode: string;
  name: string;
  email?: string;
  status: string;
  subscriptionType?: string;
  maxUsers?: number;
  maxTasks?: number;
  maxNotifications?: number;
  trialEndDate?: string;
};

const formatLimit = (value?: number) =>
  value == null ? '—' : value <= 0 ? 'Unlimited' : String(value);

const formatLimitsSummary = (record: Pick<OrgRow, 'maxUsers' | 'maxTasks' | 'maxNotifications'>) =>
  `${formatLimit(record.maxUsers)} users / ${formatLimit(record.maxTasks)} tasks / ${formatLimit(record.maxNotifications)} notifications`;

type EditForm = {
  orgCode: string;
  name: string;
  email: string;
  status: string;
  subscriptionType: string;
  maxUsers: number;
  maxTasks: number;
  maxNotifications: number;
  unlimitedUsers: boolean;
  unlimitedTasks: boolean;
  unlimitedNotifications: boolean;
  trialEndDate: string;
};

const SUBSCRIPTION_OPTIONS = [
  { value: 'TRIAL', label: 'Trial' },
  { value: 'FULL', label: 'Full' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

const emptyCreateForm = {
  name: '',
  email: '',
  adminName: '',
  adminEmail: '',
  adminMobileNumber: '',
};

const toDateInput = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'badge-success';
    case 'SUSPENDED':
      return 'badge-warning';
    default:
      return 'badge-danger';
  }
};

const OrganisationsList = () => {
  const [data, setData] = useState<OrgRow[]>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyCreateForm);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [focusedEdit, setFocusedEdit] = useState(false);
  const editFormRef = useRef<HTMLDivElement>(null);
  const focusTimeoutRef = useRef<number | null>(null);

  const load = () => {
    setLoading(true);
    fetchOrganisations(0, 50)
      .then((res: AxiosResponse) => setData(res.data.content || []))
      .catch((error) => formatError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    fetchOrganisationStatuses()
      .then((res: AxiosResponse) => setStatusOptions(res.data || []))
      .catch((error) => formatError(error));
  }, []);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        window.clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  const scrollToEditPanel = () => {
    editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  };

  useEffect(() => {
    if (!focusedEdit || !editForm) {
      return;
    }
    const timers = [0, 120, 300, 500].map(delay =>
      window.setTimeout(() => scrollToEditPanel(), delay)
    );
    return () => {
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, [focusedEdit, editForm]);

  const focusEditPanel = () => {
    setFocusedEdit(true);
    if (focusTimeoutRef.current) {
      window.clearTimeout(focusTimeoutRef.current);
    }
    focusTimeoutRef.current = window.setTimeout(() => setFocusedEdit(false), 650);
  };

  const editPanelClass = `card mb-3 border-primary org-edit-form-panel${
    focusedEdit ? ' org-edit-form-focus-flash' : ''
  }`;

  const handleStatusChange = (orgCode: string, status: string) => {
    updateOrganisation({ orgCode, status })
      .then(() => {
        formatSuccess('Organisation status updated');
        load();
      })
      .catch((error) => formatError(error));
  };

  const startEdit = (record: OrgRow) => {
    const maxUsers = record.maxUsers ?? 5;
    const maxTasks = record.maxTasks ?? 100;
    const maxNotifications = record.maxNotifications ?? 50;
    setEditForm({
      orgCode: record.orgCode,
      name: record.name || '',
      email: record.email || '',
      status: record.status || 'ACTIVE',
      subscriptionType: record.subscriptionType || 'TRIAL',
      maxUsers: maxUsers > 0 ? maxUsers : 5,
      maxTasks: maxTasks > 0 ? maxTasks : 100,
      maxNotifications: maxNotifications > 0 ? maxNotifications : 50,
      unlimitedUsers: maxUsers <= 0,
      unlimitedTasks: maxTasks <= 0,
      unlimitedNotifications: maxNotifications <= 0,
      trialEndDate: toDateInput(record.trialEndDate),
    });
    focusEditPanel();
  };

  const clearEdit = () => {
    setEditForm(null);
    setFocusedEdit(false);
  };

  const applyPlanChange = (subscriptionType: string) => {
    setEditForm(prev => {
      if (!prev) return prev;
      if (subscriptionType === 'FULL') {
        return {
          ...prev,
          subscriptionType,
          unlimitedUsers: true,
          unlimitedTasks: true,
          unlimitedNotifications: true,
        };
      }
      return {
        ...prev,
        subscriptionType,
        unlimitedUsers: false,
        unlimitedTasks: false,
        unlimitedNotifications: false,
        maxUsers: prev.maxUsers > 0 ? prev.maxUsers : 5,
        maxTasks: prev.maxTasks > 0 ? prev.maxTasks : 100,
        maxNotifications: prev.maxNotifications > 0 ? prev.maxNotifications : 50,
      };
    });
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm) return;
    updateOrganisation({
      orgCode: editForm.orgCode,
      name: editForm.name,
      email: editForm.email,
      status: editForm.status,
      subscriptionType: editForm.subscriptionType,
      maxUsers: editForm.unlimitedUsers ? 0 : Number(editForm.maxUsers),
      maxTasks: editForm.unlimitedTasks ? 0 : Number(editForm.maxTasks),
      maxNotifications: editForm.unlimitedNotifications ? 0 : Number(editForm.maxNotifications),
      trialEndDate: editForm.trialEndDate
        ? new Date(`${editForm.trialEndDate}T23:59:59`).toISOString()
        : null,
    })
      .then(() => {
        formatSuccess('Organisation and subscription updated');
        clearEdit();
        load();
      })
      .catch((error) => formatError(error));
  };

  const handleUpgrade = (orgCode: string, orgName?: string) => {
    Swal.fire({
      title: 'Upgrade subscription?',
      text: `Upgrade ${orgName || 'this organisation'} from TRIAL to FULL with unlimited users, tasks, and notifications?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, upgrade',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        upgradeSubscription(orgCode)
          .then(() => {
            formatSuccess('Organisation upgraded to FULL');
            load();
          })
          .catch((error) => formatError(error));
      }
    });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createOrganisation(form)
      .then(() => {
        formatSuccess('Organisation and org admin created');
        setForm(emptyCreateForm);
        load();
      })
      .catch((error) => formatError(error));
  };

  const columns = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Plan', dataIndex: 'subscriptionType' },
    {
      title: 'Limits',
      render: (_: unknown, record: OrgRow) => formatLimitsSummary(record),
    },
    {
      title: 'Trial ends',
      dataIndex: 'trialEndDate',
      render: (value: string) => (value ? new Date(value).toLocaleDateString() : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: string, record: OrgRow) => (
        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${statusBadgeClass(status)}`}>{status}</span>
        </div>
      ),
    },
    {
      title: 'Action',
      render: (_: unknown, record: OrgRow) => (
        <div className="d-flex gap-1 flex-wrap">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => startEdit(record)}>
            Edit
          </button>
          {record.subscriptionType === 'TRIAL' && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => handleUpgrade(record.orgCode, record.name)}
            >
              Upgrade
            </button>
          )}
        </div>
      ),
    },
  ];

  if (loading && data.length === 0) {
    return <Loader />;
  }

  return (
    <div className="page-wrapper">
      <style>{`
        @keyframes orgEditFormFocus {
          0% {
            background-color: rgba(13, 110, 253, 0.2);
            box-shadow: 0 0 0 0.35rem rgba(13, 110, 253, 0.3);
          }
          100% {
            background-color: transparent;
            box-shadow: none;
          }
        }
        .org-edit-form-panel {
          scroll-margin-top: 90px;
        }
        .org-edit-form-focus-flash {
          animation: orgEditFormFocus 0.6s ease-out forwards;
          border: 2px solid rgba(13, 110, 253, 0.85) !important;
        }
        .org-edit-form-focus-flash > .card-header,
        .org-edit-form-focus-flash > .card-body {
          animation: orgEditFormFocus 0.6s ease-out forwards;
        }
      `}</style>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Organisations</h2>
            <p className="text-muted mb-0">
              Product owner view — manage tenant organisations, status, and subscription limits.
            </p>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header">
            <h5 className="mb-0">Create Organisation</h5>
          </div>
          <div className="card-body">
            <form className="row g-3" onSubmit={handleCreate}>
              <div className="col-md-3">
                <input
                  className="form-control"
                  placeholder="Organisation name"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-3">
                <input
                  className="form-control"
                  type="email"
                  placeholder="Organisation email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="col-md-2">
                <input
                  className="form-control"
                  placeholder="Admin name"
                  value={form.adminName}
                  onChange={e => setForm({ ...form, adminName: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-2">
                <input
                  className="form-control"
                  type="email"
                  placeholder="Admin email"
                  value={form.adminEmail}
                  onChange={e => setForm({ ...form, adminEmail: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-2">
                <input
                  className="form-control"
                  placeholder="Admin mobile"
                  value={form.adminMobileNumber}
                  onChange={e => setForm({ ...form, adminMobileNumber: e.target.value })}
                  required
                />
              </div>
              <div className="col-12">
                <button type="submit" className="btn btn-primary">Create Organisation</button>
              </div>
            </form>
          </div>
        </div>

        {editForm && (
          <div ref={editFormRef} className={editPanelClass}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Edit Organisation — {editForm.name || editForm.orgCode}</h5>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearEdit}>
                Cancel
              </button>
            </div>
            <div className="card-body">
              <form className="row g-3" onSubmit={handleUpdate}>
                <div className="col-md-4">
                  <label className="form-label">Organisation name</label>
                  <input
                    className="form-control"
                    value={editForm.name}
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Organisation email</label>
                  <input
                    className="form-control"
                    type="email"
                    value={editForm.email}
                    onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={editForm.status}
                    onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <h6 className="mb-0 mt-2">Subscription configuration</h6>
                  <p className="text-muted small mb-0">
                    Full plan defaults to unlimited. Owner can set finite caps anytime (0 = unlimited).
                  </p>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Plan</label>
                  <select
                    className="form-select"
                    value={editForm.subscriptionType}
                    onChange={e => applyPlanChange(e.target.value)}
                  >
                    {SUBSCRIPTION_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Max users</label>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="unlimited-users"
                      checked={editForm.unlimitedUsers}
                      onChange={e => setEditForm({ ...editForm, unlimitedUsers: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="unlimited-users">Unlimited</label>
                  </div>
                  <input
                    className="form-control"
                    type="number"
                    min={1}
                    value={editForm.maxUsers}
                    disabled={editForm.unlimitedUsers}
                    onChange={e => setEditForm({ ...editForm, maxUsers: Number(e.target.value) })}
                    required={!editForm.unlimitedUsers}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Max tasks</label>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="unlimited-tasks"
                      checked={editForm.unlimitedTasks}
                      onChange={e => setEditForm({ ...editForm, unlimitedTasks: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="unlimited-tasks">Unlimited</label>
                  </div>
                  <input
                    className="form-control"
                    type="number"
                    min={1}
                    value={editForm.maxTasks}
                    disabled={editForm.unlimitedTasks}
                    onChange={e => setEditForm({ ...editForm, maxTasks: Number(e.target.value) })}
                    required={!editForm.unlimitedTasks}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Max notifications</label>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="unlimited-notifications"
                      checked={editForm.unlimitedNotifications}
                      onChange={e => setEditForm({ ...editForm, unlimitedNotifications: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="unlimited-notifications">Unlimited</label>
                  </div>
                  <input
                    className="form-control"
                    type="number"
                    min={0}
                    value={editForm.maxNotifications}
                    disabled={editForm.unlimitedNotifications}
                    onChange={e => setEditForm({ ...editForm, maxNotifications: Number(e.target.value) })}
                    required={!editForm.unlimitedNotifications}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Trial end date</label>
                  <input
                    className="form-control"
                    type="date"
                    value={editForm.trialEndDate}
                    onChange={e => setEditForm({ ...editForm, trialEndDate: e.target.value })}
                    disabled={editForm.subscriptionType === 'FULL'}
                  />
                  <small className="text-muted">Used for trial / expiry checks. Disabled for Full plans.</small>
                </div>
                <div className="col-12 d-flex gap-2">
                  <button type="submit" className="btn btn-primary">Save changes</button>
                  <button type="button" className="btn btn-outline-secondary" onClick={clearEdit}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <Table columns={columns} dataSource={data} rowKey="orgCode" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganisationsList;
